'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';

const FEATURED_SAFARIS = [
  { name: 'Serengeti Great Migration', location: 'Tanzania', nights: '10 nights, mobile camps', operator: 'Singita & Sanctuary', price: 'From $18,500 per guest', tags: ['Great Migration', 'Mobile Camps', 'Tanzania'] },
  { name: 'Rwanda Mountain Gorillas', location: 'Rwanda', nights: '6 nights, luxury lodges', operator: 'Singita & One&Only', price: 'From $19,800 per guest', tags: ['Gorillas', 'Rwanda', 'Rare Wildlife'] },
  { name: 'South Luangwa Walking Safari', location: 'Zambia', nights: '7 nights, small bush camps', operator: 'Robin Pope Safaris', price: 'From $12,800 per guest', tags: ['Walking Safari', 'Zambia', 'Authentic'] },
  { name: 'Sabi Sand Big Five', location: 'South Africa', nights: '5 nights, luxury lodge', operator: 'Singita, Londolozi, Royal Malewane', price: 'From $14,600 per guest', tags: ['Big Five', 'South Africa', 'Luxury Lodge'] },
  { name: 'Okavango Delta Water Safari', location: 'Botswana', nights: '8 nights, exclusive camps', operator: 'Wilderness Safaris', price: 'From $22,400 per guest', tags: ['Water Safari', 'Botswana', 'Exclusive'] },
];

const SAFARI_FEATURES = [
  { icon: '🦁', title: 'Master private guides', desc: 'Silver-level guides accompany you throughout — a rare distinction.' },
  { icon: '⛺', title: 'Exclusive-use camps', desc: 'Private mobile camps positioned along migration routes.' },
  { icon: '✈', title: 'Charter flights', desc: 'Bush flights connect every camp — no long drives.' },
];

export default function SafariPage() {
  const router = useRouter();

  return (
    <div className="min-h-screen bg-[var(--background)] text-[var(--foreground)]">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[var(--surface-2)]/95 backdrop-blur-xl border-b border-[var(--border)] h-16 flex items-center px-6">
        <Link href="/" className="text-[var(--primary)] font-bold tracking-widest uppercase mr-8" style={{ fontFamily: 'var(--font-serif)' }}>WorldwayLuxe</Link>
        <div className="flex items-center gap-6 text-sm text-[var(--muted-foreground)]">
          <Link href="/cruises" className="hover:text-[var(--primary)] transition-colors">Cruises</Link>
          <Link href="/tailor-made" className="hover:text-[var(--primary)] transition-colors">Tailor-Made</Link>
          <Link href="/villas" className="hover:text-[var(--primary)] transition-colors">Villas</Link>
          <Link href="/polar-expeditions" className="hover:text-[var(--primary)] transition-colors">Polar</Link>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <Link href="/auth/login" className="text-sm text-[var(--muted-foreground)] hover:text-[var(--foreground)] px-4 py-2">Sign In</Link>
          <Link href="/auth/register" className="btn-primary text-sm px-5 py-2 rounded-lg">Join Now</Link>
        </div>
      </nav>
      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#050810] via-[var(--background)] to-[#0D1525]" />
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(ellipse 80% 60% at 50% 20%, rgba(201,168,76,0.15) 0%, transparent 70%)' }} />
        <div className="relative z-10 max-w-5xl mx-auto px-6">
          <p className="label-caps mb-3 text-[var(--primary)]">The wild in luxury</p>
          <h1 className="text-4xl md:text-6xl font-bold mb-4" style={{ fontFamily: 'var(--font-serif)' }}>
            Safari Experiences<br />
            <span className="gold-text-gradient">Africa in Luxury.</span>
          </h1>
          <p className="text-lg text-[var(--muted-foreground)] mb-6 max-w-xl">
            Africa&apos;s most storied private conservancies and mobile camps — the Great Migration, mountain gorillas, Okavango water safaris and Cape Winelands, hosted by master guides.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
            {SAFARI_FEATURES?.map((f) => (
              <div key={f?.title} className="glass-card rounded-xl p-4 border border-[rgba(201,168,76,0.1)]">
                <div className="text-2xl mb-2">{f?.icon}</div>
                <div className="text-sm font-bold mb-1">{f?.title}</div>
                <div className="text-xs text-[var(--muted-foreground)]">{f?.desc}</div>
              </div>
            ))}
          </div>
          <div className="flex gap-4">
            <button onClick={() => router?.push('/booking?vertical=safari')} className="btn-primary px-8 py-3 rounded-xl text-sm font-semibold">Design Your Safari</button>
            <Link href="/ai-concierge" className="btn-ghost px-8 py-3 rounded-xl text-sm font-semibold border border-[var(--border)]">Ask Concierge</Link>
          </div>
        </div>
      </section>
      {/* Featured Safaris */}
      <section className="py-16 border-t border-[var(--border)]">
        <div className="max-w-5xl mx-auto px-6">
          <p className="label-caps mb-2">Featured Safaris</p>
          <h2 className="text-2xl font-bold mb-8" style={{ fontFamily: 'var(--font-serif)' }}>Africa&apos;s Most <span className="gold-text-gradient">Extraordinary Safaris</span></h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURED_SAFARIS?.map((s) => (
              <div key={s?.name} className="glass-card rounded-xl p-5 hover:border-[rgba(201,168,76,0.3)] transition-all group cursor-pointer" onClick={() => router?.push('/booking?vertical=safari')}>
                <div className="text-2xl mb-3">🦁</div>
                <h3 className="text-sm font-bold mb-1 group-hover:text-[var(--primary)] transition-colors" style={{ fontFamily: 'var(--font-serif)' }}>{s?.name}</h3>
                <p className="text-xs text-[var(--muted-foreground)] mb-1">{s?.location}</p>
                <p className="text-xs text-[var(--muted-foreground)] mb-1">{s?.nights}</p>
                <p className="text-xs text-[var(--muted-foreground)] mb-3">{s?.operator}</p>
                <div className="flex flex-wrap gap-1 mb-3">
                  {s?.tags?.map((tag) => (
                    <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full bg-[rgba(201,168,76,0.1)] text-[var(--primary)]">{tag}</span>
                  ))}
                </div>
                <p className="text-xs font-semibold text-[var(--primary)]">{s?.price}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* FAQ */}
      <section className="py-16 border-t border-[var(--border)]">
        <div className="max-w-5xl mx-auto px-6">
          <p className="label-caps mb-6">FAQ</p>
          <div className="space-y-4">
            {[
              { q: 'Are children welcome on safari?', a: 'Most lodges accept children 8+; family-specific safaris available for younger guests. We can recommend the best family-friendly properties.' },
              { q: 'What is the best time to visit?', a: 'The Great Migration peaks July–October in the Serengeti. Year-round safaris are available across Southern Africa. Our specialists will advise on the best timing for your chosen destination.' },
              { q: 'How do I design my safari?', a: 'Our specialists craft a bespoke proposal within 72 hours. Contact us or use the Trip Builder to begin.' },
            ]?.map((faq) => (
              <div key={faq?.q} className="glass-card rounded-xl p-5">
                <div className="text-sm font-bold mb-2 text-[var(--primary)]">{faq?.q}</div>
                <div className="text-xs text-[var(--muted-foreground)] leading-relaxed">{faq?.a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <footer className="border-t border-[var(--border)] py-8">
        <div className="max-w-5xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-xs text-[var(--muted-foreground)]">© 2026 Worldway Travels Group · All rights reserved.</div>
          <div className="flex items-center gap-6 text-xs text-[var(--muted-foreground)]">
            <Link href="/" className="hover:text-[var(--primary)] transition-colors">Home</Link>
            <Link href="/tailor-made" className="hover:text-[var(--primary)] transition-colors">Tailor-Made</Link>
            <Link href="/villas" className="hover:text-[var(--primary)] transition-colors">Villas</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
